import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from "@mui/material/TextField";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { SettingsButton } from "@plannel/components/ActionIconButton";
import { size, replace, cloneDeep, isNull, isUndefined, isEmpty } from "lodash"
import slowMovingInventoryService from "@plannel/services/ip/slow-moving-inventory-service";

import PaginationContainer from "@plannel/components/PaginationContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CodeUtil from "@plannel/pages/utils/code-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import i18n from "@plannel/utils/i18n";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ChartComposed from '@plannel/components/rechart/ChartComposed';
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import appConfigService from "@plannel/services/system/app-config-service";
import DateCondition from "@plannel/components/DateCondition";
import ThresholdModal from "@plannel/components/modal/ThresholdModal";

const { snackBarMessageColor } = ColorUtil;
const SlowMovingInventory = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [agedInventorySearch, setAgedInventorySearch] = useState(reduxViewState?.agedInventorySearch ?? null);

  const pageSizes = [30, 60, 100];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const resultTotalCount = useRef(null);
  const currencyMap = useRef({});

  const [slowMovingInv, setSlowMovingInv] = useState([]);
  const [slowMovingInvChart, setSlowMovingInvChart] = useState([]);
  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [sortParams, setSortParams] = useState(reduxViewState?.sortParams ?? {});
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? null);

  const [agedInventoryModalOpen, setAgedInventoryModalOpen] = useState(false);
  const [agedInventorys, setAgedInventorys] = useState([]);
  const [brushStartEndIndex, setBrushStartEndIndex] = useState({ start: null, end: null });

  const START_DATE = null;
  const END_DATE = null;
  const initPeriod = { start: reduxViewState?.period?.start ? new ZDate(reduxViewState.period.start) : START_DATE, end: reduxViewState?.period?.end ? new ZDate(reduxViewState.period.end) : END_DATE };
  const [periodSearch, setPeriodSearch] = useState(initPeriod);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [visibleColumns, setVisibleColumns] = useState([]);
  const userData = JSON.parse(localStorage.getItem("user"));
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CURRENT_DATE = new ZDate();
  const MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
  const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);

  const periodConditions = [
    { cd: "readyToSellinDt", name: "readyToSellinDt" },
    { cd: "endOfSalesDt", name: "endOfSalesDt" }
  ];

  const EXCEL_EXPORT = "excelExport";

  const classCellStyle = (params, agedInventorys) => {
    const agedInventoryQty = params.value
    let cellStyle = {};

    if (agedInventorys.length > 0) {
      if (agedInventoryQty <= agedInventorys[0].end) {
        cellStyle = { backgroundColor: ColorUtil.abcXyzTileBg("AX") };
      } else if (agedInventoryQty <= agedInventorys[1].end) {
        cellStyle = { backgroundColor: ColorUtil.abcXyzTileBg("AY") };
      } else if (agedInventoryQty <= agedInventorys[2].end) {
        cellStyle = { backgroundColor: ColorUtil.abcXyzTileBg("AZ") };
      } else if (agedInventoryQty <= agedInventorys[3].end) {
        cellStyle = { backgroundColor: ColorUtil.abcXyzTileBg("BZ") };
      } else {
        cellStyle = { backgroundColor: ColorUtil.abcXyzTileBg("CZ") };
      }
    }

    return cellStyle;
  }

  const customComparator = (valueA, valueB) => {
    if (valueA === null && valueB === null) {
        return 0;
    } else if (valueA === null) {
        return 1;
    } else if (valueB === null) {
        return -1;
    } else {
        return valueA - valueB;
    }
  };

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const columnDefs = useMemo(() => {
    return  [
      { headerName: "locDesc", field: "locDesc", type: ["nonEditableColumn"], width: 200 },
      { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], hide: true, suppressColumnsToolPanel: true, width: 200 },
      { headerName: "location", field: "locName", type: ["nonEditableColumn"], width: 200 },
      { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType' },
      { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 200 },
      { headerName: "itemCategory", field: "categoryName", type: ["nonEditableColumn"], width: 200 },
      { headerName: "currentStkQ", field: "onHandQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 170 },
      { headerName: "stockAmt", field: "onHandAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 170 },
      { headerName: "readyToSellinDt", field: "readyToSellinDt", type: ["nonEditableColumn"], comparator: (value1, value2) => customComparator(value1, value2), width: 170 },
      { headerName: "endOfSalesDt", field: "endOfSalesDt", type: ["nonEditableColumn"], comparator: (value1, value2) => customComparator(value1, value2), width: 170 },
      { headerName: "lastSoldDate", field: "lastSales", type: ["nonEditableColumn"], width: 130 },
      { headerName: "agedInventory", field: "agedInventory", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 150, cellStyle: (params) => {
        return classCellStyle(params, agedInventorys);
      } },
      { ...GridUtils.composedHeader(() => i18n.t("avgSalesQtyNMonths", { N: 12 })), field: "avgSalesMonthlyQty", type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 170 },
      { ...GridUtils.composedHeader(() => i18n.t("avgSalesAmtNMonths", { N: 12 })), field: "avgSalesMonthlyAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 170 },
      { ...GridUtils.composedHeader(() => i18n.t("invDaysNMonths", { N: 12 })), field: "inventoryTurnoverDays", type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 170, sortable: false },
      { headerName: "numOfSalesCnt", field: "numOfSalesCnt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 170 },
      { headerName: "avgNumOfSalesCnt", field: "avgNumOfSalesCnt", type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 170 },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agedInventorys]);

  const slowMovingInvChartSeries = useMemo(() => {
    return  [
      {
        id: "agradeInvQty",
        field: "agradeInvQty",
        text: agedInventorys.length > 0 ? agedInventorys[0].label : "",
        type: "bar",
        typeOptions: { stackId: "A", fill: ColorUtil.abcXyzTileBg("AX"), barSize: 120 },
        orientation: "left",
        groupName: t("stockAmt")
      },
      {
        id: "bgradeInvQty",
        field: "bgradeInvQty",
        text: agedInventorys.length > 0 ? agedInventorys[1].label : "",
        type: "bar",
        typeOptions: { stackId: "A", fill: ColorUtil.abcXyzTileBg("AY"), barSize: 120 },
        orientation: "left",
        groupName: t("stockAmt")
      },
      {
        id: "cgradeInvQty",
        field: "cgradeInvQty",
        text: agedInventorys.length > 0 ? agedInventorys[2].label : "",
        type: "bar",
        typeOptions: { stackId: "A", fill: ColorUtil.abcXyzTileBg("AZ"), barSize: 120 },
        orientation: "left",
        groupName: t("stockAmt")
      },
      {
        id: "dgradeInvQty",
        field: "dgradeInvQty",
        text: agedInventorys.length > 0 ? agedInventorys[3].label : "",
        type: "bar",
        typeOptions: { stackId: "A", fill: ColorUtil.abcXyzTileBg("BZ"), barSize: 120 },
        orientation: "left",
        groupName: t("stockAmt")
      },
      {
        id: "egradeInvQty",
        field: "egradeInvQty",
        text: agedInventorys.length > 0 ? agedInventorys[4].label : "",
        type: "bar",
        typeOptions: { stackId: "A", fill: ColorUtil.abcXyzTileBg("CZ"), barSize: 120 },
        orientation: "left",
        groupName: t("stockAmt")
      }
    ];

    // groupName 이 t() 라 언어가 바뀌면 다시 만들어야 한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agedInventorys, t]);

  const onGridReady = params => {
    setVisibleColumns(cloneDeep(GridUtils.getVisibleColumns(gridRef)));
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const onColumnVisible = () => {
    const updatedVisibleColumns = GridUtils.getVisibleColumns(gridRef);
    setVisibleColumns(cloneDeep(updatedVisibleColumns));
  };

  const retrieveExportColumns = () => {
    if (isEmpty(visibleColumns)) {
      return columnDefs;
    }
    
    return visibleColumns;
  };

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    if (selectedSearch) {
      params.condition = selectedSearch.cd;
    }

    if (periodSearch?.start) {
      params.startDate = periodSearch?.start;
    }

    if (periodSearch?.end) {
      params.endDate = periodSearch?.end;
    }

    if (agedInventorys.length > 0) {
      const thresholds = [];
      agedInventorys.forEach((agedInventory) => {
        thresholds.push(agedInventory.end);
      });

      params.thresholdList = thresholds;
    }

    if (agedInventorySearch) {
      params.start = agedInventorySearch.start;
      params.end = agedInventorySearch.end;
    }

    if (resultTotalCount.current) {
      params.totalCount = resultTotalCount.current;
    }

    params.nowDate = CURRENT_DATE;

    return { ...params, ...sortParams };
  };

  const retrieveSlowMovingInventory = () => {
    const params = getRequestParams();

    /* Brush 초기화 */
    setBrushStartEndIndex({ start: null, end: null });
    gridRef.current?.api?.setGridOption('loading', true);
    slowMovingInventoryService.getAll(params)
      .then(response => {
        const { results, message, totalPages, totalItems, currency } = response.data;
        setTotalPages(totalPages);

        if (results?.length > 0) {
          if (message) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(message) });
          }
          setSlowMovingInv(results);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setSlowMovingInv([]);
        }
        if (size(currency)) {
          currencyMap.current = currency;
        }

        resultTotalCount.current = totalItems;
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, agedInventorySearch, sortParams, selectedSearch, period: periodSearch });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = { ...pageState, ...filterState };

        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .catch(e => {
        const { response } = e;
        if (response?.data?.message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(response.data.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        }
        debug(e);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      })
  };

  const monthlyPSICheck = async () => {
    const message = await appConfigService.asyncCheckMonthlyPSIData();
    if (message) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t(message) });
    }
  }

  /* Chart Data를 가져오는 로직을 분리한 이유는 Page가 변경될때마다 데이터를 가져올 필요가 없도록 설정한 것.
  * Chart Data는 페이지를 떠나 항상 동일한 데이터를 보여주면 됨.
  */
  const retrieveSlowMovingInventoryChart = () => {
    const params = getRequestParams();

    slowMovingInventoryService.getAll({ ...params, pagination: false })
      .then(response => {
        const { results } = response.data;
        if (results?.length > 0) {
          setSlowMovingInvChart(results);
        } else {
          setSlowMovingInvChart([]);
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveSlowMovingInventoryAll = () => {
    retrieveSlowMovingInventory();
    retrieveSlowMovingInventoryChart();
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
      retrieveSlowMovingInventoryChart();
    } else if (agedInventorys?.length > 0) {
      retrieveSlowMovingInventoryAll();
    }
  }

  useEffect(() => {
    monthlyPSICheck();
    if (agedInventorys.length > 0) {
      retrieveSlowMovingInventoryChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agedInventorys]);

  useEffect(() => {
    if (gridLoading) {
      retrieveSlowMovingInventory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, agedInventorySearch, periodSearch, selectedSearch]);

  const getLabelByGrade = (obj) => {
    let label = "";

    if (obj.grade === "A") {
      label = `<= ${obj.end}`;
    } else if (obj.grade === "E") {
      label = `>= ${obj.start}`;
    } else {
      if (obj.start < obj.end) {
        label = `${obj.start} ~ ${obj.end}`;
      } else {
        label = `${obj.end} ~ ${obj.end}`;
      }
    }

    return label;
  }

  const applyAgedInventorys = selectedAgedInventoryGrades => {
    const agedInventorys = [];

    let preEndDays = -1;
    selectedAgedInventoryGrades.forEach((data) => {
      const obj = {}; 
    
      obj.grade = data.grade;
      obj.start = preEndDays + 1;
      obj.end = data.grade === "E" ? -1 : data.days;
      preEndDays = data.days;
      obj.label = getLabelByGrade(obj);

      agedInventorys.push(obj);
    }); 

    setAgedInventorys(agedInventorys);
    setAgedInventorySearch(agedInventorys.filter(agedInventory => agedInventory.grade === agedInventorySearch.grade)[0]);
  }

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <Autocomplete
          options={agedInventorys}
          getOptionLabel={option => option.label}
          value={agedInventorySearch}
          onChange={(_, newValue) => setAgedInventorySearch(newValue)}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          size="small"
          sx={{ mr: 1, minWidth: 160 }}
          renderInput={(params) => (
            <TextField {...params} label={t("agedInventory")} />
          )}
        />
        <IPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <DateCondition
          conditionList={periodConditions}
          period={periodSearch}
          maxDate={MAX_DATE}
          minDate={MIN_DATE}
          selectedSearch={selectedSearch}
          views={['year', 'month', 'day']}
          dateFormat={'yyyy-MM-dd'}
          useAutoComplete
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriodSearch}
          sx={{ mt: 0 }}
        />
        <Box sx={{ ml: "auto" }}>
          <SettingsButton label="agedInventorySetting" size="medium" onClick={() => setAgedInventoryModalOpen(true)} />
          <ExcelExportButton title={title} service={slowMovingInventoryService} columnDefs={retrieveExportColumns()}
            params={{ 
              categoryCd: filterValue?.category?.hrchyCd, 
              locationCd: filterValue?.locations?.map(location => location?.locCd), 
              item: itemSearch?.current, 
              items: [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean),
              start: agedInventorySearch?.start, 
              end: agedInventorySearch?.end, 
              condition: selectedSearch?.cd, 
              endDate: periodSearch?.end, 
              startDate: periodSearch?.start,
              ...sortParams
              }}/>
        </Box>
      </FilterContainer>      <SplitterContainer initialSizes={[2, 3]} collapseDirection='up'>
        <Box id="slow-moving-inventory-chart" className="chart-area" sx={{ height: "100%", width: "100%" }}>
          <ChartComposed
            xAxis={"categoryName"}
            dataSeries={slowMovingInvChartSeries}
            data={slowMovingInvChart}
            options={{ xAxisAngle: 0, xAxisHeight: 80, xAxisInterval: null, isAmt: true }}
            brush
            brushStartEndIndex={brushStartEndIndex}
            setBrushStartEndIndex={setBrushStartEndIndex}
          />
        </Box>
        <Box className="ag-theme-balham" sx={{ height: "calc(100% - 20px)", mt: 2, mb: 1 }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={GridUtils.getColumnDefs(columnDefs)}
            onColumnVisible={onColumnVisible}
            onColumnMoved={onColumnVisible}
            rowData={slowMovingInv}
            {...defaultGridMemo}
            onGridReady={onGridReady}
            onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
          />
        </Box>
      </SplitterContainer>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
       <ThresholdModal
        open={agedInventoryModalOpen}
        setOpen={setAgedInventoryModalOpen}
        applyThreseHolds={applyAgedInventorys}
        getLabelByGrade={getLabelByGrade}
        viewName={viewName}
        targetCd={"agedInventorys"}
      />
    </>
  );
}

export default withTranslation()(SlowMovingInventory);
